import { User } from 'lucide-react';
import React from 'react';
import type { RelatedSchemaWithFieldTypes } from '../../engage-segments-tab/segment-definitions-api';
import type { FieldInfo } from '../../engage-segments-tab/segment-definitions-api';
import { ConditionGroup } from './ConditionGroup';
import type { BuilderGroup, BuilderState } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

function generateId(): string {
  return crypto.randomUUID();
}

interface ConditionGroupListProps {
  state: BuilderState;
  contactFields: FieldInfo[];
  relatedSchemas: RelatedSchemaWithFieldTypes[];
  onUpdate: (state: BuilderState) => void;
  disabled?: boolean;
}

export const ConditionGroupList: React.FC<ConditionGroupListProps> = ({
  state,
  contactFields,
  relatedSchemas,
  onUpdate,
  disabled = false,
}) => {
  const handleGroupUpdate = (index: number) => (group: BuilderGroup) => {
    const next = [...state.groups];
    next[index] = group;
    onUpdate({ groups: next });
  };

  const handleGroupDelete = (index: number) => () => {
    const next = state.groups.filter((_, i) => i !== index);
    onUpdate({ groups: next });
  };

  const handleAddGroup = () => {
    const newGroup: BuilderGroup = {
      id: generateId(),
      logic: 'or',
      conditions: [],
    };
    onUpdate({ groups: [...state.groups, newGroup] });
  };

  return (
    <div className={styles.contactWrapper}>
      <div className={styles.contactHeader}>
        <span className={styles.contactIcon}>
          <User size={14} />
        </span>
        <span className={styles.contactTitle}>Contact</span>
      </div>
      <div className={styles.contactBody}>
        <div className={styles.groupsAndLabel}>
          {state.groups.map((group, i) => (
            <React.Fragment key={group.id}>
              {i > 0 && (
                <div className={styles.andConnector}>
                  <span className={styles.andConnectorLabel}>AND</span>
                </div>
              )}
              <ConditionGroup
                group={group}
                contactFields={contactFields}
                relatedSchemas={relatedSchemas}
                onUpdate={handleGroupUpdate(i)}
                onDelete={handleGroupDelete(i)}
                disabled={disabled}
              />
            </React.Fragment>
          ))}
        </div>
        <div className={styles.addGroupRow}>
          <button
            type="button"
            className={styles.addGroupBtn}
            onClick={handleAddGroup}
            disabled={disabled}
            aria-label="Add condition group">
            + Add condition group
          </button>
        </div>
      </div>
    </div>
  );
};
